// Auto-scroll logic for the chat view.
//
// Behavior (per Sprint 4 spec):
//   • When new messages arrive AND the user has not scrolled up → smooth
//     scroll to the bottom.
//   • When the user has scrolled up → don't auto-scroll; surface a
//     "↓ N new messages" pill.
//   • When the user manually scrolls back to the bottom → reset state.
//   • Bottom is "scrollTop + clientHeight >= scrollHeight - 8".
//
// State per agent lives in the store (`userScrolledByAgent`) so swapping
// agents doesn't lose intent.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useAppStore } from '../stores/app.store';

// Sprint chatgpt-message-rail (D4) — exported so `useMessageRail.ts` shares
// the exact same "at bottom" tolerance instead of re-declaring its own
// (single owner of the bottom threshold).
export const BOTTOM_TOLERANCE_PX = 8;

export interface UseChatScrollResult {
  containerRef: RefObject<HTMLDivElement>;
  newCount: number;
  scrollToBottom: (smooth?: boolean) => void;
  isUserScrolledUp: boolean;
}

export function useChatScroll(args: {
  agentId: string | null;
  messageCount: number;
  partialActive: boolean;
  /**
   * Sprint 15 — id of the LATEST (tail) message in the rendered set.
   * The pill bump and auto-scroll-on-new-message logic only fires when
   * this changes, so loadOlderMessages prepending older pages does NOT
   * spuriously bump the "+N new messages" pill or trigger a scroll-to-
   * bottom. Optional for back-compat; falls back to count-based logic
   * when absent.
   */
  tailId?: string | null;
}): UseChatScrollResult {
  const { agentId, messageCount, partialActive, tailId } = args;
  const containerRef = useRef<HTMLDivElement>(null);
  const lastSeenCountRef = useRef(0);
  const lastTailIdRef = useRef<string | null | undefined>(undefined);
  const [newCount, setNewCount] = useState(0);

  const isUserScrolledUp = useAppStore((s) =>
    agentId ? !!s.userScrolledByAgent[agentId] : false,
  );
  const setUserScrolled = useAppStore((s) => s.setUserScrolled);

  // Helper: are we currently parked at the bottom?
  const isAtBottom = useCallback((el: HTMLDivElement) => {
    return (
      el.scrollTop + el.clientHeight >= el.scrollHeight - BOTTOM_TOLERANCE_PX
    );
  }, []);

  // Sprint chatgpt-message-rail (EC-20, D7's "Ir al final" button visible
  // whenever `userScrolledByAgent[agentId]` is true) — that flag is
  // per-agent, in-memory, and only ever cleared by a REAL `scroll` event
  // that lands at the bottom (`onScroll` above). A conversation short
  // enough to never need scrolling never fires that event, so a stale
  // `true` (set while the content was taller, e.g. before collapsing a
  // turn or hiding developer-mode rows) survives forever and D7 would
  // paint the button permanently. Level 4 guard, one owner (this hook,
  // same `setUserScrolled` writer — not a new one, SSoT D6(4)): if the
  // container's content now fits without scrolling, the flag is wrong by
  // construction and gets cleared regardless of how it got stale.
  // `scrollHeight > 0` is mandatory: jsdom measures everything at 0, and
  // without this guard the clause would fire on every render in a test
  // that deliberately sets the flag to `true` (e.g.
  // `ConversationList.test.tsx`'s pill cases) and immediately erase it.
  const clearIfFits = useCallback(
    (el: HTMLDivElement) => {
      if (!agentId) return;
      if (el.scrollHeight > 0 && el.scrollHeight <= el.clientHeight + BOTTOM_TOLERANCE_PX) {
        setUserScrolled(agentId, false);
      }
    },
    [agentId, setUserScrolled],
  );

  const scrollToBottom = useCallback(
    (smooth = true) => {
      const el = containerRef.current;
      if (!el) return;
      el.scrollTo({
        top: el.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
      lastSeenCountRef.current = messageCount;
      setNewCount(0);
      if (agentId) setUserScrolled(agentId, false);
    },
    [messageCount, agentId, setUserScrolled],
  );

  // When the user scrolls, decide whether they are sticking to the bottom
  // or have actively scrolled up. Cheap on every scroll event.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !agentId) return;

    const onScroll = () => {
      if (isAtBottom(el)) {
        if (isUserScrolledUp) setUserScrolled(agentId, false);
        if (newCount !== 0) setNewCount(0);
        lastSeenCountRef.current = messageCount;
      } else {
        if (!isUserScrolledUp) setUserScrolled(agentId, true);
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [agentId, isAtBottom, isUserScrolledUp, messageCount, newCount, setUserScrolled]);

  // On agent switch, snap to bottom without smooth (avoids a jarring scroll
  // animation across thousands of newly-rendered messages).
  useEffect(() => {
    lastSeenCountRef.current = messageCount;
    setNewCount(0);
    const el = containerRef.current;
    if (!el) return;
    // Defer one frame so the layout has the new content's height.
    const id = requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
      // EC-20 call point (1) — after the snap, in case the new agent's
      // conversation is short enough to fit without scrolling and the
      // flag it's inheriting (per-agent, but stale flags on OTHER agents
      // are unaffected — this only clears the agent we just switched to)
      // is still `true`.
      clearIfFits(el);
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  // On new message: either auto-scroll, or bump the new-count for the pill.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !agentId) return;
    // EC-20 call point (2) — runs on every `messageCount` change, INCLUDING
    // a decrease (rows removed, e.g. toggling "developer details" off
    // hides `thinking_block`/`system` rows in one render): the early
    // return below for `delta <= 0` would otherwise skip this entirely.
    clearIfFits(el);
    const delta = messageCount - lastSeenCountRef.current;
    if (delta <= 0) return;

    // Sprint 15 — when tailId is supplied and didn't change, the delta
    // came from a prepend (loadOlderMessages added older pages at the
    // head). Skip the pill + auto-scroll; useInfiniteScrollUp handles the
    // anchor-scroll for that path.
    if (tailId !== undefined && tailId === lastTailIdRef.current) {
      lastSeenCountRef.current = messageCount;
      return;
    }
    lastTailIdRef.current = tailId ?? null;

    if (!isUserScrolledUp) {
      // The user is parked at the bottom — keep them there. Cancel the rAF
      // on unmount so we don't try to scroll a detached node in tests.
      const id = requestAnimationFrame(() => {
        if (containerRef.current === el) {
          el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        }
      });
      lastSeenCountRef.current = messageCount;
      if (newCount !== 0) setNewCount(0);
      return () => cancelAnimationFrame(id);
    } else {
      setNewCount((n) => n + delta);
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageCount, agentId, isUserScrolledUp, tailId]);

  // EC-20 call point (3) — producers that change the container's rendered
  // height WITHOUT a `messageCount` change: collapsing/expanding
  // `ConversationPanel` re-wraps the column, and turn collapse/expand
  // resizes it too. `ResizeObserver` catches every one of them uniformly
  // instead of enumerating each producer by hand. Guarded — jsdom 25 does
  // not implement `ResizeObserver`, so this is a no-op in tests, exactly
  // like `@radix-ui/react-use-size` and `@floating-ui/dom` already guard
  // for the same environment gap.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !agentId) return;
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(() => clearIfFits(el));
    observer.observe(el);
    return () => observer.disconnect();
  }, [agentId, clearIfFits]);

  // When the partial buffer changes while the user is at the bottom, keep
  // them there too (the typing indicator can grow taller than the viewport).
  useEffect(() => {
    if (!partialActive) return;
    const el = containerRef.current;
    if (!el) return;
    if (isUserScrolledUp) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
  }, [partialActive, isUserScrolledUp]);

  return { containerRef, newCount, scrollToBottom, isUserScrolledUp };
}
